import { useQuery } from "@tanstack/react-query";
import { Lock, ShieldCheck, Hourglass } from "lucide-react";
import { api } from "@/lib/api";

const STATUS = { open: "", held: "Held", booked: "Booked", past: "Passed", off: "Unavailable" };

export const SlotPicker = ({ day, treatment, value, onChange }) => {
  const { data, isFetching } = useQuery({
    queryKey: ["slots", day, treatment],
    queryFn: async () => (await api.get("/slots", { params: { day, treatment } })).data,
    enabled: !!day,
    refetchInterval: 60_000,
  });
  if (!day) return <p className="muted-sm" data-testid="slots-hint">Choose a date to see available times.</p>;
  if (!data) return <p className="muted-sm">{isFetching ? "Loading times…" : ""}</p>;
  return (
    <div className="slots" role="radiogroup" data-testid="slot-picker">
      {data.map((s) => {
        const off = ["booked", "past", "off"].includes(s.status);
        return (
          <button type="button" key={s.time} role="radio" aria-checked={value === s.time} disabled={off}
            className={`slot slot-${s.status}`} onClick={() => onChange(s.time, s.status, s.staff)} data-testid={`slot-${s.time}`}>
            {s.label}{STATUS[s.status] && <small>{STATUS[s.status]}</small>}
          </button>
        );
      })}
    </div>
  );
};

export const StaffPicker = ({ staff, value, onChange }) => (
  <div className="staff-pick" role="radiogroup" data-testid="booking-staff-picker">
    <button type="button" role="radio" aria-checked={value === "any"} className="staff-opt" onClick={() => onChange("any")} data-testid="booking-staff-any">
      <span className="staff-dot any" /> Any available
    </button>
    {staff.map((s) => (
      <button type="button" key={s.id} role="radio" aria-checked={value === s.id} className="staff-opt" onClick={() => onChange(s.id)} data-testid={`booking-staff-${s.id}`}>
        <span className="staff-dot" style={{ background: s.color }} /> {s.name}{s.state === "held" && <small>On hold</small>}
      </button>
    ))}
  </div>
);

export const TypePicker = ({ value, onChange, deposit, canDeposit, held }) => (
  <div className="types" role="radiogroup" data-testid="booking-type-picker">
    <button type="button" role="radio" aria-checked={value === "confirmed"} disabled={!canDeposit || held} className="type-card"
      onClick={() => onChange("confirmed")} data-testid="booking-type-confirmed">
      <span className="type-ic"><ShieldCheck size={18} /></span>
      <strong>Confirmed</strong>
      <span>{!canDeposit ? "Pick a specific service to pay a deposit." : held ? "This time is on hold for another client." : <>Pay a 50% deposit of <b data-testid="booking-deposit-amount">${deposit.toFixed(2)}</b> now. Next you'll choose how to pay. Your slot is reserved.</>}</span>
    </button>
    <button type="button" role="radio" aria-checked={value === "tentative"} className="type-card"
      onClick={() => onChange("tentative")} data-testid="booking-type-tentative">
      <span className="type-ic"><Hourglass size={18} /></span>
      <strong>Tentative</strong>
      <span>No payment now. We hold your time for 24 hours and email you a link to pay the 50% deposit. If it isn't paid in time, another client who pays can take the slot.</span>
    </button>
    {held && (
      <p className="type-warn" data-testid="booking-held-warning"><Lock size={13} /> Another client is holding this time for up to 24 hours. Book as tentative to be next in line, or choose another time.</p>
    )}
  </div>
);
